import {useQueryClient} from '@tanstack/react-query'

import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {Avatar} from 'primereact/avatar'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import React, {useEffect, useMemo, useRef} from 'react'
import {useParams} from 'react-router-dom'
import avatar from '../../assets/images/avatar.jpg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {checkEmptyString} from '../../utils/CommonUtils'
import {useAuth} from '../authentication/AuthProvider'
import {updateImgList} from '../detail/DetailConstant'
import {informationList} from '../form-utils/Constants'
import CustomForm from '../form-utils/CustomForm'
import {formLayout} from '../form/Constant'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {titleList} from '../patient/PatientConstants'
import StyledDetailInformationSection from '../styled-component/information/StyledDetailInformationSection'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {clientInformationList} from './ClientConstants'
import {
    clientEvent,
    clientEventTypes,
    getClientEventKey,
    onClientDetailChangeRemoveListener,
} from './broadcast/ClientEvent'
import {useClientCRUD} from './query-keys/ClientQueryManager'

const fontSize = 'text-md sm:text-md md:text-4xl lg:text-4xl'

const ClientInformation = ({data = {}}) => {
    useSignals()
    //#region Context
    const {user} = useAuth()
    const {translateCommons, translateKeys} = TranslateUtils()
    //#endregion

    //#region Param
    const {id: idString} = useParams()
    const id = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])
    //#endregion

    //#region Query
    const queryClient = useQueryClient()
    const {clientKeys} = QueryKeyManager(QueryKeyTypes.client, user?.id)

    const clientQueryKeys = useMemo(() => {
        return clientKeys.detail({id})
    }, [clientKeys, id])

    const {mutateAsync: createOrUpdateClient} = useClientCRUD(
        QueryCRUDTypes.update,
        user?.id,
    )
    //#endregion

    //#region State
    const formData = useSignal({})
    const visibleDialog = useSignal(false)
    const sectionRef = useRef(null)
    useEffect(() => {
        if (data) {
            formData.value = data
        }
    }, [data])
    //#endregion

    //#region Handle Function
    const handleOnsubmit = async submitData => {
        let toPostData = {
            ...formData.value,
            ...submitData,
        }
        if (
            !toPostData?.firstName ||
            toPostData?.firstName?.length < 0 ||
            !!checkEmptyString(toPostData?.firstName)
        ) {
            showNotification(
                severityNotification.error,
                'First Name is required !!',
            )
            return
        }

        const clientData = await createOrUpdateClient(toPostData)

        if (clientData?.id) {
            queryClient.setQueryData(clientQueryKeys, () => clientData)

            sectionRef.current?.onSuccess()

            visibleDialog.value = false
        }
    }

    useEffect(() => {
        clientEvent.on(
            `${getClientEventKey(clientEventTypes.DETAIL_CHANGE)}`,
            data => {
                if (!data || !data?.id || data?.id !== id) {
                    return
                }

                queryClient.invalidateQueries({
                    queryKey: clientQueryKeys,
                })

                formData.value = data
            },
        )

        return () =>
            onClientDetailChangeRemoveListener(clientEventTypes.DETAIL_CHANGE)
    }, [])

    const summaryOfNumbers = useMemo(() => {
        if (!data || !data?.id) {
            return []
        }

        return informationList.map(item => ({
            ...item,
            value: data[item.key],
        }))
    }, [data])

    return (
        <React.Fragment>
            {/* Header */}
            <div className=" col-12 surface-section m-0">
                <div className="flex align-items-start flex-column lg:flex-row lg:justify-content-between col-12 p-0">
                    <div className="flex align-items-start flex-column md:flex-row p-0">
                        <Avatar
                            className="mr-5 mb-3 lg:mb-0 border-circle"
                            style={{width: '120px', height: '120px'}}
                            image={data && data?.avatar ? data.avatar : avatar}
                            size="xlarge"
                            shape="circle"
                        />
                        <div className={``}>
                            <span
                                className={`font-bold ${fontSize} m-0 flex align-items-center`}
                                style={{
                                    color: 'rgba(20,184,166)',
                                }}>
                                {`${data?.firstName} ${data?.lastName}`}
                                <i className="pi pi-star ml-4 text-yellow-500"></i>
                            </span>

                            <div className="col-12 p-0">
                                <div className="flex align-items-center">
                                    <span className="font-medium text-xl">
                                        {translateKeys('Owned By')}: {` `}
                                        {!!data?.title &&
                                        data?.title !== titleList[0].value
                                            ? convertKeyToLabel(data?.title)
                                            : ''}
                                        {`  ${
                                            data?.firstName
                                                ? data.firstName
                                                : '-'
                                        } ${
                                            data?.lastName ? data.lastName : '-'
                                        }`}
                                    </span>
                                </div>
                                <div className="flex align-items-center flex-wrap text-sm gap-3">
                                    {summaryOfNumbers.map(item => (
                                        <div
                                            key={item.key}
                                            className="pr-5 mt-3">
                                            <h5 className="font-bold text-lg text-500">
                                                {translateKeys(item.label)}
                                            </h5>
                                            <div className="flex align-items-center text-700 text-xl mr-2 mt-2">
                                                {item.icon}
                                                <span>
                                                    {item.value
                                                        ? item.value
                                                        : '0'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 lg:mt-0">
                        <Button
                            icon="pi pi-fw pi-images"
                            className="p-button-rounded mr-2"
                            onClick={() => (visibleDialog.value = true)}
                        />
                    </div>
                </div>
            </div>
            {/* Information */}
            <div className="grid p-0">
                <div className="col-12 p-0">
                    <StyledDetailInformationSection
                        ref={sectionRef}
                        data={data}
                        dataList={clientInformationList}
                        onSubmit={handleOnsubmit}
                    />
                </div>
            </div>

            {/* Dialog */}
            <Dialog
                header={
                    <h5 className="mb-0 font-bold">
                        <strong>{translateKeys('Change Avatar')}</strong>
                    </h5>
                }
                style={{width: '40vw'}}
                breakpoints={{
                    '960px': '75vw',
                    '641px': '100vw',
                }}
                visible={visibleDialog.value}
                onHide={() => (visibleDialog.value = false)}>
                <CustomForm
                    onSubmit={handleOnsubmit}
                    layout={formLayout.inline}
                    dataList={updateImgList}
                />
            </Dialog>
        </React.Fragment>
    )
}

export default ClientInformation
